import { collectEntityTargetExtentPoints } from '@toolproof-core/visualization/rendering/_pure/frame-bounds';
import type { EntityItem } from '@toolproof-core/visualization/_contract/item';
import type { Vector3Tuple } from '@toolproof-core/visualization/_contract/geometry';
import type { SpaceDerivationFrame } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { buildToolStepItemKey, parseWorkflowStateEntryItemKey } from '@/workflow/_pure/keyCodec';
import type { CaseMetadata } from '@/workflow/_contract/kernel';
import type { BaseConfig, TorusConfig } from '@/workflow/_contract/config';
import { COLOR_PALETTES } from '@/workflow/_data/config/fragments/colorPalettes';
import type { WorkflowGrouping } from '@/workflow-runner/_contract/episode';

type ProfilePoint = [number, number];

function roundedProfile(corners: ProfilePoint[], rounding: number, segments: number): ProfilePoint[] {
    if (rounding === 0) return corners;
    return corners.flatMap((corner, index) => {
        const previous = corners[(index + corners.length - 1) % corners.length];
        const next = corners[(index + 1) % corners.length];
        const toward = (point: ProfilePoint): ProfilePoint => {
            const distance = Math.hypot(point[0] - corner[0], point[1] - corner[1]);
            const fraction = Math.min(rounding / distance, 0.25);
            return [corner[0] + (point[0] - corner[0]) * fraction, corner[1] + (point[1] - corner[1]) * fraction];
        };
        const from = toward(previous);
        const to = toward(next);
        return Array.from({ length: segments + 1 }, (_, sample): ProfilePoint => {
            const t = sample / segments;
            return [
                (1 - t) ** 2 * from[0] + 2 * t * (1 - t) * corner[0] + t ** 2 * to[0],
                (1 - t) ** 2 * from[1] + 2 * t * (1 - t) * corner[1] + t ** 2 * to[1],
            ];
        });
    });
}

function fitCaseProfile(points: Vector3Tuple[], settings: TorusConfig, whenIsInner?: boolean, whenHeightRatio = 1): ProfilePoint[] {
    const radialPoints = points.map(([x, y, z]): ProfilePoint => [Math.hypot(x, z), Math.abs(y)]);
    const inner = Math.min(...radialPoints.map(([radius]) => radius));
    const outer = Math.max(...radialPoints.map(([radius]) => radius));
    const width = outer - inner;
    if (!Number.isFinite(width) || width <= 0 || inner <= 0
        || radialPoints.some(([radius, height]) => !Number.isFinite(radius) || !Number.isFinite(height))) {
        throw new Error('Case envelope requires finite member geometry with a positive central opening.');
    }
    const heightFactor = (radius: number) => {
        if (whenIsInner === undefined) return 1;
        const towardWhat = whenIsInner ? (radius - inner) / width : (outer - radius) / width;
        return whenHeightRatio + (1 - whenHeightRatio) * towardWhat;
    };
    const height = Math.max(...radialPoints.map(([radius, y]) => y / heightFactor(radius)));
    // Leave clearance for rounded corners and the inscribed facets of the revolution.
    const clearance = Math.max(width * settings.clearanceFraction,
        outer * (1 / Math.cos(Math.PI / settings.segments) - 1) * 2, settings.minimumClearance);
    const slope = whenIsInner === undefined ? 0 : height * (1 - whenHeightRatio) / width;
    const verticalClearance = clearance * Math.hypot(1, slope);
    const innerRadius = inner - clearance;
    const outerRadius = outer + clearance;
    if (innerRadius <= 0) throw new Error('Case envelope clearance would close its central opening.');
    const innerHeight = height * heightFactor(innerRadius) + verticalClearance;
    const outerHeight = height * heightFactor(outerRadius) + verticalClearance;
    return roundedProfile([
        [innerRadius, -innerHeight], [outerRadius, -outerHeight],
        [outerRadius, outerHeight], [innerRadius, innerHeight],
    ], clearance * settings.cornerRoundingFraction, settings.cornerSegments);
}

function validateTorusConfig(settings: TorusConfig, path: string): void {
    if (typeof settings.enabled !== 'boolean'
        || !Number.isFinite(settings.opacity) || settings.opacity < 0 || settings.opacity > 1
        || !Number.isFinite(settings.clearanceFraction) || settings.clearanceFraction < 0
        || !Number.isFinite(settings.minimumClearance) || settings.minimumClearance <= 0
        || !Number.isFinite(settings.cornerRoundingFraction) || settings.cornerRoundingFraction < 0 || settings.cornerRoundingFraction > 1
        || !Number.isInteger(settings.cornerSegments) || settings.cornerSegments < 1
        || !Number.isInteger(settings.segments) || settings.segments < 16) {
        throw new Error(`Invalid workflow ${path} settings.`);
    }
}

/** Case profiles taper toward WHEN; optional MacroStep envelopes enclose their owners' profiles. */
export function withCaseTori(args: {
    frame: SpaceDerivationFrame;
    memberships: readonly CaseMetadata[];
    traceHandle: string;
    config: BaseConfig;
    grouping?: WorkflowGrouping;
    macroStepOwnerByKey?: ReadonlyMap<string, string>;
}): SpaceDerivationFrame {
    const caseDefaults = args.config.domainPresentation.entities.cases.tori;
    const macroDefaults = args.config.domainPresentation.entities.macroSteps.tori;
    const caseSettings = args.grouping === undefined ? caseDefaults
        : { ...caseDefaults, enabled: args.grouping === 'cases' };
    const macroSettings = args.grouping === undefined ? macroDefaults
        : { ...macroDefaults, enabled: args.grouping === 'macroSteps' };
    validateTorusConfig(caseSettings, 'domainPresentation.entities.cases.tori');
    validateTorusConfig(macroSettings, 'domainPresentation.entities.macroSteps.tori');
    if (!Number.isFinite(caseSettings.whenHeightRatio) || caseSettings.whenHeightRatio <= 0 || caseSettings.whenHeightRatio > 1) {
        throw new Error('Workflow domainPresentation.entities.cases.tori.whenHeightRatio must be greater than zero and at most one.');
    }
    if (args.memberships.length === 0
        || (!caseSettings.enabled && !macroSettings.enabled)) return args.frame;
    const colors = COLOR_PALETTES[args.config.colorPalette];
    const frame: SpaceDerivationFrame = {
        ...args.frame,
        itemCollections: [...args.frame.itemCollections],
        constellations: [...args.frame.constellations],
        itemParametricState: {
            ...args.frame.itemParametricState,
            entityItemByKey: { ...args.frame.itemParametricState?.entityItemByKey },
        },
        constellationParametricState: {
            ...args.frame.constellationParametricState,
            entityConstellationByKey: { ...args.frame.constellationParametricState?.entityConstellationByKey },
        },
    };
    const geometryFrame: SpaceDerivationFrame = {
        ...args.frame,
        itemParametricState: {
            ...args.frame.itemParametricState,
            entityItemByKey: Object.fromEntries(Object.entries(args.frame.itemParametricState?.entityItemByKey ?? {})
                .map(([key, state]) => [key, { ...state, label: undefined }])),
        },
    };
    const appendEnvelope = (suffix: string, name: string, profile: ProfilePoint[], macroStep: boolean) => {
        const settings = macroStep ? macroSettings : caseSettings;
        const itemKey = `item:entity:${suffix}` as const;
        const itemCollectionKey = `collection:entity:${suffix}` as const;
        const constellationKey = `constellation:entity:${suffix}` as const;
        frame.itemCollections.push({
            itemCollectionKind: 'entity', itemCollectionKey,
            items: [{ itemKind: 'entity', itemKey, name }],
        });
        frame.constellations.push({
            constellationKind: 'entity', constellationKey, itemCollectionKey,
            anchor: { itemKey, anchorKey: 'center' },
        });
        frame.itemParametricState!.entityItemByKey![itemKey] = {
            visualStyle: { color: macroStep ? colors.macroSteps.tori.color : colors.cases.tori.color, opacity: settings.opacity },
            interaction: { enabled: false },
            shape: {
                shapeKind: 'revolved-profile',
                labelHostCapability: 'above-only',
                profile, segments: settings.segments,
                anchorsByKey: { center: { anchorKey: 'center', localPosition: [0, 0, 0] } },
            },
        };
        frame.constellationParametricState!.entityConstellationByKey![constellationKey] = {
            layout: {
                layoutKind: 'spoke', anchor: { anchorKind: 'origin' },
                originNearestPosition: [0, 0, 0], direction: [0, 0, 1], spacing: 0,
            },
        };
    };
    const profilesByOwner = new Map<string, ProfilePoint[]>();
    for (const membership of args.memberships) {
        const stepKeys = new Set([membership.whenToolStepKey, membership.whatToolStepKey]);
        const ringKeys = new Set([...stepKeys].map(buildToolStepItemKey));
        const rodScopes = [...stepKeys].map((key) => `:primitive:${encodeURIComponent(`${args.traceHandle}:${key}`)}:`);
        const selected = new Set<EntityItem['itemKey']>();
        const points: Vector3Tuple[] = [];
        for (const collection of args.frame.itemCollections) {
            if (collection.itemCollectionKind !== 'entity') continue;
            for (const item of collection.items) {
                if (ringKeys.has(item.itemKey)) {
                    const constellation = args.frame.constellations.find((entry) =>
                        entry.constellationKind === 'entity' && entry.itemCollectionKey === collection.itemCollectionKey);
                    const layout = constellation?.constellationKind === 'entity'
                        ? args.frame.constellationParametricState?.entityConstellationByKey?.[constellation.constellationKey]?.layout
                        : undefined;
                    if (layout?.layoutKind !== 'concentric-ring') {
                        throw new Error(`Case torus requires a concentric ToolStep ring for ${item.itemKey}.`);
                    }
                    const index = collection.items.indexOf(item);
                    const radius = layout.ringRadii?.[index] ?? layout.startRadius + index * layout.ringGap;
                    const tube = layout.ringTube.tubeRadii?.[index] ?? layout.ringTube.tubeRadius;
                    const crossSection = layout.ringTube.crossSectionsByRing?.[index] ?? 'tube';
                    if (crossSection !== 'tube') {
                        const ratio = layout.ringTube.bandThicknessRatio ?? 0.15;
                        const broad = layout.ringTube.bandWidth === undefined ? tube : layout.ringTube.bandWidth / 2;
                        const thin = layout.ringTube.bandThickness === undefined ? tube * ratio : layout.ringTube.bandThickness / 2;
                        const radial = crossSection === 'vertical-band' ? thin : broad;
                        const height = crossSection === 'horizontal-band' ? thin : broad;
                        const toothDepth = layout.ringTube.bandTeeth?.depth ?? 0;
                        const outerRadial = radial + (crossSection === 'horizontal-band' ? toothDepth : 0);
                        const outerHeight = height + (crossSection === 'vertical-band' ? toothDepth : 0);
                        points.push([0, -outerHeight, radius - outerRadial], [0, outerHeight, radius - outerRadial],
                            [0, -outerHeight, radius + outerRadial], [0, outerHeight, radius + outerRadial]);
                        continue;
                    }
                    // Circumscribe the tube cross-section without treating the whole ring as a sphere.
                    const extent = tube / Math.cos(Math.PI / 32);
                    for (let sample = 0; sample < 32; sample += 1) {
                        const angle = sample * Math.PI * 2 / 32;
                        points.push([0, extent * Math.sin(angle), radius + extent * Math.cos(angle)]);
                    }
                } else {
                    const role = parseWorkflowStateEntryItemKey(item.itemKey);
                    if ((role && [...stepKeys].some((key) => key === role.stepKey))
                        || rodScopes.some((scope) => item.itemKey.includes(scope))) selected.add(item.itemKey);
                }
            }
        }
        points.push(...collectEntityTargetExtentPoints(geometryFrame, selected));
        if (points.length === 0) throw new Error('Case torus has no member geometry.');
        const profile = fitCaseProfile(points, caseSettings, membership.whenStepIndex < membership.whatStepIndex, caseSettings.whenHeightRatio);
        if (caseSettings.enabled) {
            appendEnvelope(`runner:case-torus:${membership.ownerMacroStepKey}:${membership.ownerCaseIndex}`,
                `Case ${membership.ownerCaseIndex} envelope`, profile, false);
        }
        if (macroSettings.enabled) {
            const owner = args.macroStepOwnerByKey?.get(membership.ownerMacroStepKey) ?? membership.ownerMacroStepKey;
            const profiles = profilesByOwner.get(owner) ?? [];
            profiles.push(...profile);
            profilesByOwner.set(owner, profiles);
        }
    }
    for (const [owner, profiles] of profilesByOwner) {
        const profile = fitCaseProfile(profiles.map(([radius, height]): Vector3Tuple => [0, height, radius]), macroSettings);
        appendEnvelope(`runner:macro-step-torus:${owner}`, 'MacroStep envelope', profile, true);
    }
    return frame;
}
